import { toDate } from "shared/mappers";
import { toAbsoluteImageUrl } from "shared/utils";
import { periodsFromDto } from "meetings/student-meeting/api/mappers/studentMeetingManage.mapper";
import type { RatingGradeType } from "meetings/shared/types";
import type {
  RatingHistoryItem,
  RatingHistoryItemDto,
  RatingSaveDto,
  StudentMeeting,
  StudentMeetingDto,
  StudentMeetingLessongroup,
  StudentMeetingLessongroupDto,
  StudentMeetingRating,
  StudentMeetingRatingDto,
  StudentMeetingRatingStudentDto,
  StudentMeetingTimeframe,
  StudentMeetingTimeframeDto,
} from "meetings/student-meeting/types";

const studentFromDto = (
  dto: StudentMeetingRatingStudentDto,
): StudentMeetingRating["student"] => ({
  id: dto.id,
  user: {
    firstName: dto.user.first_name ?? null,
    middleName: dto.user.middle_name ?? null,
    lastName: dto.user.last_name ?? null,
    profileImage: toAbsoluteImageUrl(dto.user.profile_image),
  },
});

// A missing grades key means the meeting does not allow grades
// (MEETING-GRADES-001) — kept as null so the page can hide the badges
// entirely instead of showing "geen cijfer" placeholders.
const gradesFromDto = (
  dto: StudentMeetingRatingDto,
): StudentMeetingRating["grades"] => {
  if (!dto.grades) {
    return null;
  }
  return dto.grades
    .filter(
      (grade): grade is { type: RatingGradeType; grade: string } =>
        grade.type === "SE" || grade.type === "rapport",
    )
    .map((grade) => ({ type: grade.type, grade: grade.grade }));
};

const ratingFromDto = (dto: StudentMeetingRatingDto): StudentMeetingRating => ({
  id: dto.id,
  studentId: dto.student_id,
  lessonClass: dto.lesson_class,
  subjectShortage: dto.subject_shortage,
  subjectName: dto.subject_name ?? null,
  rating: dto.rating,
  ratingRemark: dto.rating_remark,
  ratingAction: dto.rating_action,
  teacherShortage: dto.teacher_shortage ?? null,
  student: studentFromDto(dto.student),
  grades: gradesFromDto(dto),
  actionplanId: dto.actionplan_id ?? null,
});

const lessongroupFromDto = (
  dto: StudentMeetingLessongroupDto,
): StudentMeetingLessongroup => ({
  name: dto.name,
  ratings: dto.ratings.map(ratingFromDto),
});

const timeframeFromDto = (
  dto: StudentMeetingTimeframeDto | null | undefined,
  autoShare: number | boolean | null | undefined,
): StudentMeetingTimeframe | null =>
  dto
    ? {
        closedNoModifyAt: toDate(dto.closed_no_modify_at),
        closedAt: toDate(dto.closed_at),
        opensAt: toDate(dto.open_at),
        closedModifyAt: toDate(dto.closed_modify_at),
        archivedAt: toDate(dto.archived_at),
        shareFeedbackAt: toDate(dto.share_feedback_at),
        // A missing flag must not promise automatic sharing (CATWISE-1223 #12).
        autoShare: autoShare === true || autoShare === 1,
        triangleFrom: toDate(dto.triangle_conversation_from),
        triangleTill: toDate(dto.triangle_conversation_till),
        feedbackReservations: periodsFromDto(dto.feedback_reservations),
        speedDatePreparations: periodsFromDto(dto.speed_date_preparations),
        speedDates: periodsFromDto(dto.speed_dates),
      }
    : null;

export const studentMeetingFromDto = (
  dto: StudentMeetingDto,
): StudentMeeting => ({
  id: dto.id,
  name: dto.name,
  status: dto.status,
  meetingType: dto.meeting_type,
  message: dto.message ?? null,
  totalRatings: dto.total_ratings ?? null,
  totalGivenRatings: dto.total_given_ratings ?? null,
  lessongroups: (dto.lessongroups ?? []).map(lessongroupFromDto),
  timeframe: timeframeFromDto(
    dto.timeframe_settings ?? dto.timeframes,
    dto.auto_share,
  ),
});

export const ratingHistoryFromDto = (
  dto: RatingHistoryItemDto,
): RatingHistoryItem => ({
  meetingName: dto.name ?? null,
  schoolYear: dto.school_year ?? null,
  teacherShortage: dto.teacher_shortage ?? null,
  // Over half of the ratings rows carry only a shortage (subject_name was
  // added later), so fall back to it rather than showing no subject at all.
  subjectName: dto.subject_name || dto.subject_shortage || null,
  rating: dto.rating,
  ratingRemark: dto.rating_remark ?? null,
  ratingAction: dto.rating_action ?? null,
  allowStudentRatings: dto.allow_student_ratings ?? false,
  studentRating: dto.student_rating ?? null,
});

/**
 * The endpoint blanks `name` on every row of a (meeting, subject) group except
 * the last one, because the legacy history block prints the meeting name once,
 * below the group. The dialog renders a card per row, so each needs its own
 * title: re-fill the blanks from the row below, which is by contract the same
 * group (blanking only happens when the next row belongs to it).
 */
export const backfillMeetingNames = (
  items: RatingHistoryItem[],
): RatingHistoryItem[] => {
  const filled = [...items];
  for (let index = filled.length - 2; index >= 0; index--) {
    if (filled[index].meetingName === null) {
      filled[index] = {
        ...filled[index],
        meetingName: filled[index + 1].meetingName,
      };
    }
  }
  return filled;
};

/**
 * Projects a rating back to the snake_case shape the backend and the legacy
 * AI suggestions component expect.
 */
export const ratingToDto = (rating: StudentMeetingRating): RatingSaveDto => ({
  id: rating.id,
  student_id: rating.studentId,
  subject_shortage: rating.subjectShortage,
  teacher_shortage: rating.teacherShortage,
  rating: rating.rating,
  rating_remark: rating.ratingRemark,
  rating_action: rating.ratingAction,
});
